import React, { useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix webpack default icon paths
delete L.Icon.Default.prototype._getIconUrl;

function BoundsController({ cameras }) {
  const map = useMap();
  useEffect(() => {
    if (cameras.length === 0) return;
    if (cameras.length === 1) {
      map.setView([parseFloat(cameras[0].gpsLat), parseFloat(cameras[0].gpsLng)], 15);
      return;
    }
    const bounds = L.latLngBounds(
      cameras.map(c => [parseFloat(c.gpsLat), parseFloat(c.gpsLng)])
    );
    map.fitBounds(bounds, { padding: [60, 60] });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function makeIcon(color, isSelected) {
  const size = isSelected ? 22 : 16;
  const border = isSelected ? 'border:2px solid #ffffff;' : '';
  const glow = isSelected
    ? `box-shadow:0 0 0 4px rgba(244,123,32,0.25),0 0 12px ${color};`
    : `box-shadow:0 0 8px ${color};`;
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;background:${color};border-radius:50%;${border}${glow}box-sizing:border-box;"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    tooltipAnchor: [size / 2, -size / 2],
  });
}

export default function MapView({ cameras, selectedCamera, onSelectCamera }) {
  const camerasWithGPS = cameras.filter(c => {
    const lat = parseFloat(c.gpsLat);
    const lng = parseFloat(c.gpsLng);
    return !isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
  });

  if (camerasWithGPS.length === 0) {
    return (
      <div style={{
        height: '100%', width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: '16px', color: 'var(--text-muted)',
      }}>
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.3">
          <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
          <line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/>
        </svg>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '14px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          No cameras with GPS coordinates
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: '11px', opacity: 0.5, textAlign: 'center' }}>
          Edit a camera and add GPS Lat/Lng to show it on the map
        </div>
      </div>
    );
  }

  const center = [parseFloat(camerasWithGPS[0].gpsLat), parseFloat(camerasWithGPS[0].gpsLng)];

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <MapContainer
        center={center}
        zoom={14}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <BoundsController cameras={camerasWithGPS} />
        {camerasWithGPS.map(cam => {
          const isSelected = selectedCamera?.id === cam.id;
          const isOnline = cam.status?.online;
          const color = isSelected ? '#f47b20' : isOnline ? '#00ff88' : '#ff3366';
          return (
            <Marker
              key={cam.id}
              position={[parseFloat(cam.gpsLat), parseFloat(cam.gpsLng)]}
              icon={makeIcon(color, isSelected)}
              eventHandlers={{ click: () => onSelectCamera(cam) }}
            >
              <Tooltip direction="top" offset={[0, -4]}>
                <div style={{ fontFamily: 'sans-serif', fontSize: '12px', lineHeight: 1.5 }}>
                  <div style={{ fontWeight: 700 }}>{cam.name}</div>
                  {cam.location && <div style={{ color: '#666', fontSize: '11px' }}>{cam.location}</div>}
                  <div style={{ fontSize: '11px', color: isOnline ? '#00aa55' : '#cc2244' }}>
                    ● {isOnline ? 'Online' : 'Offline'}
                  </div>
                  <div style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>
                    {parseFloat(cam.gpsLat).toFixed(5)}°N, {parseFloat(cam.gpsLng).toFixed(5)}°E
                  </div>
                </div>
              </Tooltip>
            </Marker>
          );
        })}
      </MapContainer>

      {/* Legend */}
      <div style={{
        position: 'absolute', bottom: '32px', left: '16px', zIndex: 1000,
        background: 'rgba(10,14,20,0.88)',
        border: '1px solid var(--border-dim)',
        borderRadius: 'var(--radius-md)',
        padding: '8px 12px',
        display: 'flex', gap: '16px',
        fontFamily: 'var(--font-mono)', fontSize: '10px',
        pointerEvents: 'none',
      }}>
        <span style={{ color: 'var(--status-online)' }}>● Online</span>
        <span style={{ color: 'var(--accent-red)' }}>● Offline</span>
        <span style={{ color: 'var(--accent-orange)' }}>● Selected</span>
      </div>
    </div>
  );
}
